"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var react_intl_1 = require("react-intl");
var core_1 = require("@material-ui/core");
var useForceUpdate_1 = require("../../../../common/hooks/useForceUpdate");
exports.useCustomErrorMessagesMapper = function (formData) {
    var intl = react_intl_1.useIntl();
    var _a = core_1.useTheme().checkoutLite.enquiryForm, hasCustomErrorPriority = _a.hasCustomErrorPriority, hasCustomPhoneErrorMessage = _a.hasCustomPhoneErrorMessage;
    var forceUpdate = useForceUpdate_1.default().forceUpdate;
    var getErrorDomain = function (errorPath, isParentDomain) {
        if (isParentDomain === void 0) { isParentDomain = false; }
        var pathArray = errorPath.split('.');
        return isParentDomain ? pathArray[1] : pathArray[pathArray.length - 1];
    };
    var isFieldDirty = function (domain, formData) {
        var _a;
        var isInCypressIframe = window.Cypress && ((_a = window.Cypress) === null || _a === void 0 ? void 0 : _a.spec);
        if (isInCypressIframe && typeof domain === 'string')
            return true;
        if (!formData || typeof formData !== 'object') {
            return false;
        }
        if (domain in formData) {
            return !!formData[domain];
        }
        return Object.values(formData).some(function (value) {
            return isFieldDirty(domain, value);
        });
    };
    var filterDirtyFields = function (errors, formData) {
        if (!formData)
            return errors;
        return errors.filter(function (error) {
            var domain = getErrorDomain(error.property);
            return isFieldDirty(domain, formData);
        });
    };
    var prioritizeError = function (errorDomain, errorName, errors, defaultMessage) {
        if (errorDomain === 'phone' && errorName === 'maxLength' && hasCustomPhoneErrorMessage) {
            var errorNames = errors.map(function (error) { return error.name; });
            if (errorNames.includes('maxLength') && (errorNames.includes('regexp') || errorNames.includes('pattern'))) {
                return intl.formatMessage({ id: 'trans__checkout__enquiry_form__error_messages__phone_format' });
            }
        }
        if (errorDomain === 'identificationNumber' && errorName === 'maxLength') {
            var errorIdentificationType = errors.reduce(function (filteredValue, current) {
                if (current.property === '.identificationNumberByType.identificationType') {
                    filteredValue.push(current.params.allowedValues[0]);
                }
                return filteredValue;
            }, []);
            var errorMaxLength = errors.reduce(function (filteredValue, current) {
                if (current.name === 'maxLength') {
                    filteredValue.push(current.params.limit);
                }
                return filteredValue;
            }, []);
            if (!errorIdentificationType.some(function (val) { return val === 'RUC'; }) && !errorMaxLength.some(function (val) { return val === 11; })) {
                return intl.formatMessage({
                    id: 'trans__checkout__enquiry_form__error_messages__identification_number_format',
                });
            }
        }
        return defaultMessage;
    };
    var getInputValue = function (property, formData) {
        var properties = property.split('.').filter(function (prop) { return prop !== ''; });
        var value = formData;
        for (var _i = 0, properties_1 = properties; _i < properties_1.length; _i++) {
            var prop = properties_1[_i];
            if (value && typeof value === 'object' && prop in value) {
                value = value[prop];
            }
            else {
                value = undefined;
                break;
            }
        }
        return value;
    };
    /**
     * Calls a force update function whenever the formData state changes.
     * This is needed because transformErrors callback is called before the latest formData state is updated,
     * so a force update is required to ensure that the latest state is used.
     */
    react_1.useEffect(function () {
        forceUpdate();
    }, [formData]);
    return react_1.useCallback(function (errors) { var _a; return (_a = filterDirtyFields(errors, formData)) === null || _a === void 0 ? void 0 : _a.map(function (error) {
        var _a, _b;
        switch (error.name) {
            case 'required':
                error.message = intl.formatMessage({ id: 'trans__checkout__enquiry_form__error_messages__required_field' });
                break;
            case 'pattern':
            case 'regexp':
                switch (getErrorDomain(error.property)) {
                    case 'firstName':
                    case 'lastName':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__alphabetic_format',
                        });
                        break;
                    case 'phone':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__phone_format',
                        });
                        break;
                    case 'identificationNumberRut':
                    case 'userId':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__identification_number_format',
                        });
                        break;
                    case 'identificationNumber':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__identification_number_format',
                        });
                        break;
                    case 'email':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__email_format',
                        });
                        break;
                    case 'drivingLicenseDate':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__drivers_license_date',
                        });
                        break;
                    case 'maritalStatus':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__marital_status',
                        });
                        break;
                    case 'model':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__alphabetic_numeric_format',
                        });
                        break;
                    case 'year':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__year_format',
                        });
                        break;
                    case 'mileage':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__mileage_number_format',
                        });
                        break;
                    case 'registrationNumber':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__registration_number_format',
                        });
                        break;
                    default:
                        error.message = intl.formatMessage({ id: 'trans__checkout__enquiry_form__error_messages__default' });
                        break;
                }
                break;
            case 'minLength':
                switch (getErrorDomain(error.property)) {
                    case 'year':
                        var value = getInputValue(error.property, formData);
                        if (/^\d+$/.test(value)) {
                            error.message = intl.formatMessage({
                                id: 'trans__checkout__enquiry_form__error_messages__min_year_length',
                            });
                        }
                        else {
                            error.message = intl.formatMessage({
                                id: 'trans__checkout__enquiry_form__error_messages__year_format',
                            });
                        }
                        break;
                    case 'registrationNumber':
                        error.message = intl.formatMessage({
                            id: 'trans__checkout__enquiry_form__error_messages__registration_number_format',
                        });
                        break;
                    default:
                        error.message = intl.formatMessage({ id: 'trans__checkout__enquiry_form__error_messages__default' });
                }
                break;
            case 'maxLength':
                error.message = intl.formatMessage({ id: 'trans__checkout__enquiry_form__error_messages__max_length' }, { limit: (_a = error.params) === null || _a === void 0 ? void 0 : _a.limit });
                if (hasCustomErrorPriority) {
                    error.message = prioritizeError(getErrorDomain(error.property), 'maxLength', errors, intl.formatMessage({ id: 'trans__checkout__enquiry_form__error_messages__max_length' }, { limit: (_b = error.params) === null || _b === void 0 ? void 0 : _b.limit }));
                }
                break;
            default:
                error.message = intl.formatMessage({
                    id: 'trans__checkout__enquiry_form__error_messages__default',
                    defaultMessage: ' ',
                });
                break;
        }
        return error;
    }); }, [formData]);
};
